import { Box, Skeleton, Stack, type Theme, Typography } from '@mui/material';
import { useState } from 'react';
import { useGrowersDashboard } from '/@/hooks/dashboards';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import GrowersDashboardCard from '../../../components/dashboard-cards/growers-dashboard-card';
import RecommendationsDashboardCard from '../../../components/dashboard-cards/recommendations-dashboard-card';
import NewFieldGrowersModal from '../../../growers/components/new-field-modal';
import { ONBOARING_IDS } from '../../home';
import CreateGrowerSection from '../create-grower-section';

const growersCardSx = {
    border: (t: Theme) => `1px solid ${t.palette.divider}`,
    padding: 2,
    backgroundColor: '#FFFFFF',
};

const ActivitySection = ({
    setOpenCreateRecommendationModal,
    onGrowersHelpClick,
}: {
    setOpenCreateRecommendationModal: React.Dispatch<React.SetStateAction<boolean>>;
    onGrowersHelpClick?: () => void;
}) => {
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);

    const userNodeQuery = useOwnUserNode();
    const userNodeId = userNodeQuery.data?.id;
    const isAdvisor = useIsValidatedAdvisor();
    const isRV = useIsValidatedRV();
    const growersQuery = useGrowersDashboard({
        node: userNodeId,
        nodeType: 'grower',
        excludeTargetNode: true,
    });
    const growerCount = growersQuery.data?.count ?? 0;
    const hasGrowers = growerCount > 0;
    const showCreateGrower = !hasGrowers;
    const showAdvisorOnlyEmptyState = showCreateGrower && (isAdvisor || isRV);
    const isContentReady = userNodeId && !userNodeQuery.isValidating && !growersQuery.isValidating;

    return (
        <Stack spacing={3} sx={{ flex: 1, width: '100%', minHeight: 0 }}>
            <Stack spacing={1}>
                <Typography color='primary' variant='h5'>
                    {_('Tu actividad con productores')}
                </Typography>
                <Typography color='primary.mui.inactive' variant='body2'>
                    {_('Accedé a la información clave para acompañar a cada productor.')}
                </Typography>
            </Stack>
            {isContentReady ? (
                <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                    <Box
                        id={ONBOARING_IDS.FIRST}
                        sx={
                            showCreateGrower
                                ? {
                                      ...growersCardSx,
                                      ...(showAdvisorOnlyEmptyState && {
                                          flex: 1,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          minHeight: 0,
                                      }),
                                  }
                                : undefined
                        }
                    >
                        {showCreateGrower ? (
                            <CreateGrowerSection onAddFields={() => setNewFieldModalOpen(true)} />
                        ) : (
                            <GrowersDashboardCard
                                userNodeId={userNodeId}
                                nodeScope={getNodeScopeFilters('growers')}
                                onHelpClick={onGrowersHelpClick}
                            />
                        )}
                    </Box>
                    {!showAdvisorOnlyEmptyState && (
                        <Box id={ONBOARING_IDS.FOURTH}>
                            <RecommendationsDashboardCard
                                setOpenCreateRecommendationModal={setOpenCreateRecommendationModal}
                                userNodeId={userNodeId}
                                nodeScope={getNodeScopeFilters('growers')}
                            />
                        </Box>
                    )}
                </Stack>
            ) : (
                <Skeleton variant='rectangular' height='500px' width='100%' />
            )}
            <NewFieldGrowersModal
                open={newFieldModalOpen}
                setOpen={setNewFieldModalOpen}
                rvUserNodeId={userNodeId}
                trackEntrySource='growers-list-dashboard'
            />
        </Stack>
    );
};

export default ActivitySection;
